import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { ConfirmationDialog } from '../components/ui/confirmation-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import {
  FolderOpen, FolderPlus, Upload, Search, FileText, File, Image, FileSpreadsheet,
  MoreVertical, Download, Pencil, FolderInput, Archive, ArchiveRestore, Trash2,
  Loader2, Eye, EyeOff, Plus, X, ChevronRight,
} from 'lucide-react';

function formatBytes(bytes) {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

function formatDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
}

function fileIcon(contentType) {
  if (!contentType) return File;
  if (contentType.startsWith('image/')) return Image;
  if (contentType.includes('pdf')) return FileText;
  if (contentType.includes('sheet') || contentType.includes('csv') || contentType.includes('excel')) return FileSpreadsheet;
  return File;
}

export default function Documents() {
  const { api } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFolder, setActiveFolder] = useState(null); // null = all
  const [rootDocCount, setRootDocCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  // Dialogs
  const [uploadOpen, setUploadOpen] = useState(false);
  const [renameDoc, setRenameDoc] = useState(null);
  const [moveDoc, setMoveDoc] = useState(null);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [renameFolderData, setRenameFolderData] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, doc: null });
  const [deleteFolderConfirm, setDeleteFolderConfirm] = useState({ open: false, folder: null });

  // Upload state
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadFolder, setUploadFolder] = useState('');
  const [uploadVisibleToWorkers, setUploadVisibleToWorkers] = useState(false);
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Form state for rename/folder
  const [renameValue, setRenameValue] = useState('');
  const [moveFolderValue, setMoveFolderValue] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [renameFolderValue, setRenameFolderValue] = useState('');

  // ── Fetch ───────────────────────────────────────────────────────
  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeFolder) params.set('folder', activeFolder);
      if (searchQuery) params.set('search', searchQuery);
      if (showArchived) params.set('archived', 'true');
      const res = await api.get(`/documents?${params.toString()}`);
      const docs = res.data.documents || [];
      setDocuments(docs);
      // When viewing root without a search filter, capture the stable total
      if (!activeFolder && !searchQuery) {
        setRootDocCount(docs.length);
      }
    } catch {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [api, activeFolder, searchQuery, showArchived]);

  // Fetch folders ONLY on initial mount — all mutations use optimistic local
  // state updates so the sidebar never flickers or empties mid-operation.
  const fetchFolders = useCallback(async () => {
    try {
      const res = await api.get('/documents/folders');
      const incoming = res.data.folders || [];
      // Merge: never shrink the list from an in-flight response — union with
      // what we already have locally so optimistic additions survive.
      setFolders(prev => {
        const merged = Array.from(new Set([...prev, ...incoming]));
        merged.sort((a, b) => a.localeCompare(b));
        return merged;
      });
    } catch { /* ignore */ }
  }, [api]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);
  useEffect(() => { fetchFolders(); }, [fetchFolders]);

  // ── Upload handler ──────────────────────────────────────────────
  const handleUpload = async () => {
    if (!uploadFiles.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      for (const f of uploadFiles) fd.append('files', f);
      if (uploadFolder && uploadFolder !== '__none') fd.append('folder', uploadFolder);
      fd.append('visible_to_workers', uploadVisibleToWorkers);
      if (uploadDescription) fd.append('description', uploadDescription);
      const res = await api.post('/documents/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Uploaded ${res.data.count} file(s)`);
      setUploadOpen(false);
      setUploadFiles([]);
      setUploadFolder('');
      setUploadVisibleToWorkers(false);
      setUploadDescription('');
      // Only refresh documents — folders don't change from an upload
      fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // ── Actions ─────────────────────────────────────────────────────
  const toggleVisibility = async (doc) => {
    try {
      await api.put(`/documents/${doc.id}`, { visible_to_workers: !doc.visible_to_workers });
      fetchDocuments();
    } catch { toast.error('Failed to update visibility'); }
  };

  const handleRename = async () => {
    if (!renameDoc || !renameValue.trim()) return;
    try {
      await api.put(`/documents/${renameDoc.id}`, { filename: renameValue.trim() });
      toast.success('Renamed');
      setRenameDoc(null);
      fetchDocuments();
    } catch { toast.error('Failed to rename'); }
  };

  const handleMove = async () => {
    if (!moveDoc) return;
    try {
      await api.put(`/documents/${moveDoc.id}`, { folder: (moveFolderValue && moveFolderValue !== '__none') ? moveFolderValue : null });
      toast.success('Moved');
      setMoveDoc(null);
      // Only refresh documents — the folder list doesn't change when moving
      // a file between existing folders.
      fetchDocuments();
    } catch { toast.error('Failed to move'); }
  };

  const handleArchive = async (doc) => {
    try {
      await api.post(`/documents/${doc.id}/archive`);
      toast.success('Archived');
      fetchDocuments();
    } catch { toast.error('Failed to archive'); }
  };

  const handleRestore = async (doc) => {
    try {
      await api.post(`/documents/${doc.id}/restore`);
      toast.success('Restored');
      fetchDocuments();
    } catch { toast.error('Failed to restore'); }
  };

  const handleDelete = async () => {
    if (!deleteConfirm.doc) return;
    try {
      await api.delete(`/documents/${deleteConfirm.doc.id}`);
      toast.success('Permanently deleted');
      setDeleteConfirm({ open: false, doc: null });
      fetchDocuments();
    } catch { toast.error('Failed to delete'); }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await api.post('/documents/folders', { action: 'create', name: newFolderName.trim() });
      setFolders(prev => [...prev, newFolderName.trim()].sort());
      setNewFolderOpen(false);
      setNewFolderName('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleRenameFolder = async () => {
    if (!renameFolderData || !renameFolderValue.trim()) return;
    try {
      await api.post('/documents/folders', { action: 'rename', old_name: renameFolderData, name: renameFolderValue.trim() });
      toast.success('Folder renamed');
      const oldName = renameFolderData;
      const newName = renameFolderValue.trim();
      setRenameFolderData(null);
      if (activeFolder === oldName) setActiveFolder(newName);
      // Optimistic local rename — swap old name for new, re-sort
      setFolders(prev => prev.map(f => f === oldName ? newName : f).sort((a, b) => a.localeCompare(b)));
      fetchDocuments();
    } catch { toast.error('Failed to rename folder'); }
  };

  const handleDeleteFolder = async () => {
    if (!deleteFolderConfirm.folder) return;
    const folderToDelete = deleteFolderConfirm.folder;
    try {
      const res = await api.delete(`/documents/folders/${encodeURIComponent(folderToDelete)}`);
      toast.success(`Folder deleted — ${res.data.moved_to_root} file(s) moved to root`);
      setDeleteFolderConfirm({ open: false, folder: null });
      // Optimistic removal — only strip the deleted folder, keep everything else
      setFolders(prev => prev.filter(f => f !== folderToDelete));
      if (activeFolder === folderToDelete) setActiveFolder(null);
      fetchDocuments();
    } catch { toast.error('Failed to delete folder'); }
  };

  const dropzoneRef = useRef(null);
  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length) { setUploadFiles(files); setUploadOpen(true); }
  };

  return (
    <div className="p-6 space-y-0 max-w-[1400px] mx-auto" data-testid="documents-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Documents</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Company-wide files, templates and policies</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowArchived(!showArchived)} data-testid="toggle-archived-btn">
            {showArchived ? <ArchiveRestore className="h-4 w-4 mr-1.5" /> : <Archive className="h-4 w-4 mr-1.5" />}
            {showArchived ? 'View Active' : 'View Archive'}
          </Button>
          <Button size="sm" onClick={() => setUploadOpen(true)} data-testid="upload-documents-btn">
            <Upload className="h-4 w-4 mr-1.5" /> Upload
          </Button>
        </div>
      </div>

      <div className="flex gap-5">
        {/* ── Folder sidebar ── */}
        <div className="w-56 flex-shrink-0 space-y-1" data-testid="folder-sidebar">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold uppercase text-muted-foreground tracking-wider">Folders</span>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { setNewFolderName(''); setNewFolderOpen(true); }} data-testid="create-folder-btn">
              <FolderPlus className="h-3.5 w-3.5" />
            </Button>
          </div>
          <button
            type="button"
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${activeFolder === null ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted/50 text-muted-foreground'}`}
            onClick={() => setActiveFolder(null)}
            data-testid="folder-all"
          >
            <FolderOpen className="h-4 w-4" /> All Documents
            <Badge variant="secondary" className="ml-auto text-[10px] px-1.5">{activeFolder === null ? documents.length : rootDocCount}</Badge>
          </button>
          {folders.map(f => (
            <div key={f} className="group relative">
              <button
                type="button"
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${activeFolder === f ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted/50 text-muted-foreground'}`}
                onClick={() => setActiveFolder(f)}
                data-testid={`folder-${f}`}
              >
                <FolderOpen className="h-4 w-4" />
                <span className="truncate flex-1 text-left">{f}</span>
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="absolute right-1 top-1.5 h-6 w-6 p-0 opacity-0 group-hover:opacity-100" data-testid={`folder-menu-${f}`}>
                    <MoreVertical className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => { setRenameFolderData(f); setRenameFolderValue(f); }}>
                    <Pencil className="h-3.5 w-3.5 mr-2" /> Rename
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-destructive" onClick={() => setDeleteFolderConfirm({ open: true, folder: f })}>
                    <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete Folder
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ))}
        </div>

        {/* ── Main content ── */}
        <div className="flex-1 min-w-0" ref={dropzoneRef} onDrop={handleDrop} onDragOver={e => e.preventDefault()}>
          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search files..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9 h-9"
              data-testid="document-search-input"
            />
          </div>

          {showArchived && (
            <div className="flex items-center gap-2 mb-3 p-2 bg-amber-500/10 border border-amber-500/25 rounded text-sm text-amber-500">
              <Archive className="h-4 w-4" /> Viewing archived documents. Archived files still consume storage — hard-delete to free space.
            </div>
          )}

          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : documents.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <FileText className="h-10 w-10 mb-3 opacity-30" />
                  <p className="text-sm">{showArchived ? 'No archived documents' : 'No documents yet'}</p>
                  {!showArchived && (
                    <Button variant="outline" size="sm" className="mt-3" onClick={() => setUploadOpen(true)}>
                      <Upload className="h-3.5 w-3.5 mr-1.5" /> Upload your first document
                    </Button>
                  )}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[40%]">Name</TableHead>
                      <TableHead>Folder</TableHead>
                      <TableHead>Uploaded By</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Size</TableHead>
                      <TableHead className="text-center w-24">Workers</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {documents.map(doc => {
                      const Icon = fileIcon(doc.content_type);
                      return (
                        <TableRow key={doc.id} className="group" data-testid={`doc-row-${doc.id}`}>
                          <TableCell>
                            <div className="flex items-center gap-2.5 min-w-0">
                              <Icon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                              <div className="min-w-0">
                                <a href={doc.blob_url} target="_blank" rel="noopener noreferrer"
                                  className="text-sm font-medium hover:underline truncate block" data-testid={`doc-link-${doc.id}`}>
                                  {doc.filename}
                                </a>
                                {doc.description && <p className="text-xs text-muted-foreground truncate">{doc.description}</p>}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {doc.folder ? (
                              <Badge variant="outline" className="text-[10px]">{doc.folder}</Badge>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">{doc.uploaded_by_name || '—'}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{formatDate(doc.created_at)}</TableCell>
                          <TableCell className="text-right text-sm text-muted-foreground">{formatBytes(doc.file_size)}</TableCell>
                          <TableCell className="text-center">
                            <Switch
                              checked={doc.visible_to_workers}
                              onCheckedChange={() => toggleVisibility(doc)}
                              data-testid={`doc-visibility-${doc.id}`}
                            />
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`doc-menu-${doc.id}`}>
                                  <MoreVertical className="h-3.5 w-3.5" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => window.open(doc.blob_url, '_blank')}>
                                  <Download className="h-3.5 w-3.5 mr-2" /> Download
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => { setRenameDoc(doc); setRenameValue(doc.filename); }}>
                                  <Pencil className="h-3.5 w-3.5 mr-2" /> Rename
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => { setMoveDoc(doc); setMoveFolderValue(doc.folder || ''); }}>
                                  <FolderInput className="h-3.5 w-3.5 mr-2" /> Move to Folder
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {showArchived ? (
                                  <DropdownMenuItem onClick={() => handleRestore(doc)}>
                                    <ArchiveRestore className="h-3.5 w-3.5 mr-2" /> Restore
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem onClick={() => handleArchive(doc)}>
                                    <Archive className="h-3.5 w-3.5 mr-2" /> Archive
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem className="text-destructive" onClick={() => setDeleteConfirm({ open: true, doc })}>
                                  <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete Permanently
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ── Upload Dialog ── */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-md" data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Upload className="h-5 w-5" /> Upload Documents</DialogTitle>
            <DialogDescription>Select files to upload to the document library.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div
              className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); const files = Array.from(e.dataTransfer?.files || []); if (files.length) setUploadFiles(files); }}
              data-testid="upload-dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={e => setUploadFiles(Array.from(e.target.files || []))}
                data-testid="upload-file-input"
              />
              {uploadFiles.length > 0 ? (
                <div className="space-y-1">
                  {uploadFiles.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="truncate">{f.name}</span>
                      <span className="text-xs text-muted-foreground ml-auto">{formatBytes(f.size)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Click to browse or drag files here</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Folder</Label>
              <Select value={uploadFolder} onValueChange={setUploadFolder}>
                <SelectTrigger data-testid="upload-folder-select"><SelectValue placeholder="None (root)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">None (root)</SelectItem>
                  {folders.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Description (optional)</Label>
              <Input value={uploadDescription} onChange={e => setUploadDescription(e.target.value)} placeholder="Brief description" data-testid="upload-description" />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={uploadVisibleToWorkers} onCheckedChange={setUploadVisibleToWorkers} data-testid="upload-visible-toggle" />
              <Label className="text-sm">Visible to workers on mobile</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setUploadOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={handleUpload} disabled={uploading || !uploadFiles.length} data-testid="upload-submit-btn">
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
              Upload {uploadFiles.length > 0 ? `(${uploadFiles.length})` : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Rename Dialog ── */}
      <Dialog open={!!renameDoc} onOpenChange={v => { if (!v) setRenameDoc(null); }}>
        <DialogContent className="max-w-sm" data-testid="rename-dialog">
          <DialogHeader><DialogTitle>Rename Document</DialogTitle></DialogHeader>
          <Input value={renameValue} onChange={e => setRenameValue(e.target.value)} data-testid="rename-input" />
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setRenameDoc(null)}>Cancel</Button>
            <Button size="sm" onClick={handleRename} data-testid="rename-submit-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Move to Folder Dialog ── */}
      <Dialog open={!!moveDoc} onOpenChange={v => { if (!v) setMoveDoc(null); }}>
        <DialogContent className="max-w-sm" data-testid="move-dialog">
          <DialogHeader><DialogTitle>Move to Folder</DialogTitle></DialogHeader>
          <Select value={moveFolderValue} onValueChange={setMoveFolderValue}>
            <SelectTrigger><SelectValue placeholder="None (root)" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">None (root)</SelectItem>
              {folders.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setMoveDoc(null)}>Cancel</Button>
            <Button size="sm" onClick={handleMove} data-testid="move-submit-btn">Move</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── New Folder Dialog ── */}
      <Dialog open={newFolderOpen} onOpenChange={setNewFolderOpen}>
        <DialogContent className="max-w-sm" data-testid="new-folder-dialog">
          <DialogHeader><DialogTitle>Create Folder</DialogTitle></DialogHeader>
          <Input value={newFolderName} onChange={e => setNewFolderName(e.target.value)} placeholder="Folder name" data-testid="new-folder-input" />
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setNewFolderOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={handleCreateFolder} data-testid="new-folder-submit-btn">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Rename Folder Dialog ── */}
      <Dialog open={!!renameFolderData} onOpenChange={v => { if (!v) setRenameFolderData(null); }}>
        <DialogContent className="max-w-sm" data-testid="rename-folder-dialog">
          <DialogHeader><DialogTitle>Rename Folder</DialogTitle></DialogHeader>
          <Input value={renameFolderValue} onChange={e => setRenameFolderValue(e.target.value)} data-testid="rename-folder-input" />
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setRenameFolderData(null)}>Cancel</Button>
            <Button size="sm" onClick={handleRenameFolder} data-testid="rename-folder-submit-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete Document Confirmation ── */}
      <ConfirmationDialog
        open={deleteConfirm.open}
        onOpenChange={v => setDeleteConfirm(c => ({ ...c, open: v }))}
        title="Delete Permanently?"
        description={`This will permanently delete "${deleteConfirm.doc?.filename}" and remove it from storage. This cannot be undone.`}
        onConfirm={handleDelete}
        confirmText="Delete"
        variant="destructive"
      />

      {/* ── Delete Folder Confirmation ── */}
      <ConfirmationDialog
        open={deleteFolderConfirm.open}
        onOpenChange={v => setDeleteFolderConfirm(c => ({ ...c, open: v }))}
        title="Delete Folder?"
        description={`This will delete the folder "${deleteFolderConfirm.folder}". Files in this folder will be moved to the root — no files will be deleted.`}
        onConfirm={handleDeleteFolder}
        confirmText="Delete Folder"
        variant="destructive"
      />
    </div>
  );
}
